import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';

import { SingleSelect } from '@els/els-react--select';
import { Autosuggest } from '@els/els-react--autosuggest';
import { TAXONOMY_BOUND_OPERATORS } from '../constants';

import './TaxonomyLookupModalHeader.scss';

const mapTaxonomySuggestion = (item) => ({
  ...item,
  inputValue: item?.value ?? item?.title ?? '',
});

function TaxonomyLookupModalHeader({
  opModel,
  search,
  getSuggestions,
  boundOperator,
  searchByKeyword,
  selectBoundOperator,
  totalHitCount,
  suggestInit,
}) {
  const autosuggestRef = useRef(null);
  const [searchValue, setSearchValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);

  const loadSuggestions = React.useMemo(
    () =>
      debounce((value) => {
        if (!value?.trim()) {
          setSuggestions([]);
          return;
        }

        Promise.resolve(getSuggestions(value))
          .then((items) => {
            const mappedSuggestions = (items || []).map(mapTaxonomySuggestion);
            setSuggestions(mappedSuggestions);
          })
          .catch(Logger.reject);
      }, 500),
    [getSuggestions]
  );

  useEffect(() => {
    if (search === searchValue || (!search && search !== '')) {
      return;
    }

    setSearchValue(search);
    autosuggestRef.current?.setTextInputValue(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleSearchSelect = (item) => {
    const selectedValue = item?.inputValue ?? item?.value ?? '';
    setSearchValue(selectedValue);
    autosuggestRef.current?.setTextInputValue(selectedValue);
  };

  const taxonomyOperators = Object.keys(TAXONOMY_BOUND_OPERATORS);
  const operatorsDropdownOptions = taxonomyOperators?.map((operator) => ({
    label: TAXONOMY_BOUND_OPERATORS[operator],
    value: operator,
  }));

  const handleSearch = (valueOrEvent) => {
    let value = '';

    if (typeof valueOrEvent === 'string') {
      value = valueOrEvent;
    } else {
      value = valueOrEvent?.inputValue ?? valueOrEvent?.value ?? valueOrEvent?.target?.value ?? searchValue;
    }

    searchByKeyword(value);
  };

  const handleInputClear = () => {
    setSearchValue('');
    autosuggestRef.current?.setTextInputValue('');
    setSuggestions([]);
    searchByKeyword('');
  };

  const handleBoundOperatorSelect = (value) => {
    selectBoundOperator(value);
  };

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      handleInputClear();
      setSearchValue('');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="taxonomyLookupModal__header">
      <div className="taxonomyLookupModal__header_wrapper taxonomyLookupModal__header_wrapper-main">
        <h4 className="rx-newmodal-title taxonomyLookupModal__header_title">{opModel.title}</h4>
        {!!totalHitCount && <div className="taxonomyLookupModal__header_badge">{totalHitCount}</div>}
      </div>

      <div className="taxonomyLookupModal__header_wrapper u-els-margin-left">
        <div className="taxonomyLookupModal__header_dropdown">
          <SingleSelect
            id="taxonomyLookupModal__header_dropdown"
            className="e2e-taxonomyLookupModal-header-dropdown"
            options={operatorsDropdownOptions}
            selectedOptions={[boundOperator || operatorsDropdownOptions[0]?.value]}
            label="Select bound operator"
            required
            data-e2e="taxonomyLookupModal-header-bound-operator-select"
            onSelect={({ value }) => {
              handleBoundOperatorSelect(value);
            }}
            size="small"
            shouldHideLabel
          />
        </div>
      </div>

      <div className="taxonomyLookupModal__header_wrapper taxonomyLookupModal__header_wrapper-main">
        <div className="taxonomyLookupModal__header_form">
          <Autosuggest
            ref={autosuggestRef}
            className="e2e-taxonomyLookupModal-header-input"
            shouldAlwaysChooseSuggestion={false}
            shouldCloseOnESC
            suggestions={suggestions}
            textInputProps={{
              id: 'taxonomyLookupModal__header_input',
              label: 'Enter search term',
              placeholder: 'Enter search term',
              shouldHideLabel: true,
              autoFocus: !!suggestInit.search,
              iconLeft: {
                id: 'taxonomyLookupModal__header_input-search',
                sprite: 'Search',
                children: 'Search',
                a11y: { name: 'TaxonomyLookup modal search' },
              },
              iconLeftCallback: handleSearch,
              iconRight: searchValue
                ? {
                    id: 'taxonomyLookupModal__header_input-clear',
                    sprite: 'Close',
                    isDecorative: true,
                    children: 'Clear search input',
                  }
                : undefined,
              iconRightCallback: searchValue ? handleInputClear : undefined,
              onKeyDown: (event) => {
                if (event.key === 'Enter') {
                  handleSearch();
                }
              },
            }}
            onChange={(value) => {
              setSearchValue(value);
              loadSuggestions(value);
            }}
            onSelectSuggestion={(result, value) => {
              const selectedItem = value || result;
              const selectedValue = selectedItem?.inputValue ?? selectedItem?.value ?? '';
              handleSearchSelect(selectedItem);
              handleSearch(selectedValue);
            }}
          />
        </div>
      </div>
    </div>
  );
}

TaxonomyLookupModalHeader.propTypes = {
  opModel: PropTypes.object,
  suggestInit: PropTypes.object,
  boundOperator: PropTypes.string,
  search: PropTypes.string,
  getSuggestions: PropTypes.func.isRequired,
  searchByKeyword: PropTypes.func.isRequired,
  selectBoundOperator: PropTypes.func.isRequired,
  totalHitCount: PropTypes.number,
};

export default TaxonomyLookupModalHeader;
